import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  CommandDataOut,
  CommandQueryDataIn,
  CommandStatus,
  PageInfo,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { AuthService } from '../../../services/auth.service';
import { SignalRService } from '../../../services/signalr.service';
import { SearchComponent } from '../../core/components/search/search.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { StatusComponent } from '../../core/components/status/status.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRBaseComponent } from '../../../services/signalr-base.component';

@Component({
  selector: 'app-on-demand-custom-register',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    AngularSvgIconModule,
    SortComponent,
    SearchComponent,
    RouterLink,
    PaginationComponent,
    IsNewDirective,
    StatusComponent,
  ],
  templateUrl: './on-demand-custom-register.component.html',
  styleUrl: './on-demand-custom-register.component.scss',
})
export class OnDemandCustomRegisterComponent extends SignalRBaseComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  commandStatuses = Object.values(CommandStatus).map(s => s.toUpperCase());

  data: CommandDataOut[] | undefined = [];
  PermissionEnum = PermissionsEnum;
  readonly dayInMs = 24 * 60 * 60 * 1000;

  requestData = new CommandQueryDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    status: undefined,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
    public authService: AuthService,
    signalRService: SignalRService,
    public clipboardService: ClipboardService
  ) {
    super(signalRService);
    this.reloadTable(1);
  }

  protected override registerHandlers(): void {
    this.registerSignalREvent('on-demand-generic-attribute-read', (message) => {
      const item = this.data?.find((item) => item.id === message.commandId);
      if (item) {
        if (message.commandStatus == CommandStatus.Failed || message.commandStatus == CommandStatus.ErrorOccured) {
          try {
            const parsed = JSON.parse(message.errorMessage);
            item.errorMessage = parsed.Message;
          } catch {
            item.errorMessage = message.errorMessage;
          }
        }

        // Logic to handle 'Finalize' status specifically for this component
        if (message.commandStatus === 'Finalize') {
          item.status = CommandStatus.Success;
        } else {
          item.status = message.commandStatus;
        }
      }
    });

    // Silence the 'online-status' warning broadcasted to device groups
    this.registerSignalREvent('online-status', () => {});
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }
  
  printResponseOfCommand(event: MouseEvent, cmd: CommandDataOut) {
    if (cmd.status === CommandStatus.Failed) {
      event.stopPropagation();
      this.toastr.error(cmd.errorMessage);
    }
  }

  retryCommand(cmd: CommandDataOut) {
    this.client.onDemandLoadProfileRegisterRetry(cmd.id!).subscribe({
      next: () => {
        this.toastr.success('Command retry initiated successfully');
        this.reloadTable(this.currentPage);
      },
      error: (err: string) => {
        this.toastr.error('Failed to retry command: ' + err);
      },
    });
  }

  handleRowClick(
    commandId: string | undefined,
    meterNo: string | undefined,
    commandStatus: CommandStatus | undefined,
  ) {
    if (commandStatus !== CommandStatus.Failed)
      this.router.navigate(['on-demand/custom-register-details'], {
        queryParams: { commandId: commandId, meterNo: meterNo },
      });
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;
    this.client.onDemandCustomRegisterCommands(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  onStatusOption(selectedStatus?: string) {
    if (selectedStatus !== undefined) {
      this.requestData.status =
        selectedStatus === 'ALL'
          ? undefined
          : (selectedStatus as CommandStatus);
    }
    this.reloadTable();
  }
}
